import { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Group,
  Text,
  ActionIcon,
  Collapse,
  Stack,
  Button,
  Badge,
  Loader,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconNotes,
  IconPlus,
  IconTrash,
  IconSnowflake,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import type { AmendmentCommentContext } from "../../hooks/useAmendmentDiagnosticDraft";
import RichTextDiagnosis, { stripHtml } from "./RichTextDiagnosis";
import {
  usePathologistComments,
  useAddPathologistComment,
  useDeletePathologistComment,
} from "../../hooks/useWorkflows";
import type { PathologistComment } from "../../services/workflows.service";

interface CaseLevelSectionProps {
  caseId: string | null;
  /** Case-level frozen interpretation rich text (single field on the case) */
  frozenInterpretation: string;
  onFrozenInterpretationChange: (value: string) => void;
  isReadOnly?: boolean;
  amendmentCommentContext?: AmendmentCommentContext;
}

export function CaseLevelSection({
  caseId,
  frozenInterpretation,
  onFrozenInterpretationChange,
  isReadOnly,
  amendmentCommentContext,
}: CaseLevelSectionProps) {
  const [expanded, setExpanded] = useState(true);
  const [frozenExpanded, setFrozenExpanded] = useState(true);
  const [showAddComment, setShowAddComment] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: canonicalComments = [], isLoading: commentsLoading } =
    usePathologistComments(caseId ?? undefined);
  const comments = amendmentCommentContext?.comments ?? canonicalComments;
  const addComment = useAddPathologistComment();
  const deleteComment = useDeletePathologistComment();

  const handleAddComment = useCallback(async () => {
    if (!caseId || !newCommentText.trim()) return;
    try {
      if (amendmentCommentContext)
        await amendmentCommentContext.addComment(newCommentText);
      else
        await addComment.mutateAsync({ caseId, commentText: newCommentText });
      setNewCommentText("");
      setShowAddComment(false);
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to save comment",
        color: "red",
      });
    }
  }, [addComment, amendmentCommentContext, caseId, newCommentText]);

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      if (!caseId) return;
      setDeletingId(commentId);
      try {
        if (amendmentCommentContext)
          await amendmentCommentContext.deleteComment(commentId);
        else await deleteComment.mutateAsync({ caseId, commentId });
      } catch {
        notifications.show({
          title: "Error",
          message: "Failed to delete comment",
          color: "red",
        });
      } finally {
        setDeletingId(null);
      }
    },
    [amendmentCommentContext, caseId, deleteComment]
  );

  const formatCommentAuthor = (comment: PathologistComment) => {
    if (!comment.createdBy) return "Unknown";
    const { fullName, credentials } = comment.createdBy;
    return credentials ? `${fullName}, ${credentials}` : fullName;
  };

  return (
    <Box
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      {/* ── Pathologist Comments card ────────────────────────────── */}
      <Paper
        withBorder
        style={{ flex: "1 1 400px", minWidth: 0, maxWidth: 650 }}
      >
        <Group
          justify="space-between"
          px="md"
          py="xs"
          style={{
            cursor: "pointer",
            borderBottom: expanded
              ? "1px solid var(--mantine-color-gray-3)"
              : undefined,
          }}
          onClick={() => setExpanded((v) => !v)}
        >
          <Group gap="xs">
            <IconNotes size={16} color="var(--mantine-color-blue-6)" />
            <Text size="sm" fw={600} tt="uppercase" c="dimmed">
              Pathologist Comments
            </Text>
            {comments.length > 0 && (
              <Badge size="xs" variant="light" color="blue">
                {comments.length}
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            {!isReadOnly && expanded && (
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconPlus size={12} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddComment((v) => !v);
                  setNewCommentText("");
                }}
              >
                Add Comment
              </Button>
            )}
            <ActionIcon variant="subtle" size="sm">
              {expanded ? (
                <IconChevronUp size={14} />
              ) : (
                <IconChevronDown size={14} />
              )}
            </ActionIcon>
          </Group>
        </Group>

        <Collapse in={expanded}>
          <Box px="md" py="sm">
            {commentsLoading ? (
              <Loader size="xs" />
            ) : (
              <Stack gap="xs">
                {comments.map((c) => (
                  <Paper key={c.id} p="xs" withBorder bg="gray.0">
                    <Group
                      justify="space-between"
                      align="flex-start"
                      wrap="nowrap"
                    >
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Box
                          className="widget-rich-text"
                          style={{ wordBreak: "break-word" }}
                          dangerouslySetInnerHTML={{ __html: c.commentText }}
                        />
                        <Text size="xs" c="dimmed" mt={4}>
                          {formatCommentAuthor(c)} &mdash;{" "}
                          {dayjs(c.createdAt).format("MM/DD/YYYY h:mm A")}
                        </Text>
                      </Box>
                      {!isReadOnly && (
                        <Tooltip label="Delete comment">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="red"
                            loading={deletingId === c.id}
                            onClick={() => handleDeleteComment(c.id)}
                          >
                            <IconTrash size={12} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Paper>
                ))}

                {comments.length === 0 && !showAddComment && (
                  <Text size="sm" c="dimmed" fs="italic">
                    No comments
                  </Text>
                )}

                {showAddComment && (
                  <Box>
                    <RichTextDiagnosis
                      value={newCommentText}
                      onChange={setNewCommentText}
                      placeholder="Enter pathologist comment..."
                      minHeight={80}
                      autoFocus
                    />
                    <Group justify="flex-end" mt="xs" gap="xs">
                      <Button
                        size="xs"
                        variant="default"
                        onClick={() => {
                          setShowAddComment(false);
                          setNewCommentText("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        loading={addComment.isPending}
                        disabled={!newCommentText.trim()}
                        onClick={handleAddComment}
                      >
                        Save
                      </Button>
                    </Group>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* ── Frozen Interpretation card ──────────────────────────── */}
      <Paper
        withBorder
        style={{ flex: "1 1 400px", minWidth: 0, maxWidth: 650 }}
      >
        <Group
          justify="space-between"
          px="md"
          py="xs"
          style={{
            cursor: "pointer",
            borderBottom: frozenExpanded
              ? "1px solid var(--mantine-color-gray-3)"
              : undefined,
          }}
          onClick={() => setFrozenExpanded((v) => !v)}
        >
          <Group gap="xs">
            <IconSnowflake size={16} color="var(--mantine-color-cyan-6)" />
            <Text size="sm" fw={600} tt="uppercase" c="dimmed">
              Frozen Interpretation
            </Text>
            {stripHtml(frozenInterpretation).trim() && (
              <Badge size="xs" variant="light" color="cyan">
                entered
              </Badge>
            )}
          </Group>
          <ActionIcon variant="subtle" size="sm">
            {frozenExpanded ? (
              <IconChevronUp size={14} />
            ) : (
              <IconChevronDown size={14} />
            )}
          </ActionIcon>
        </Group>

        <Collapse in={frozenExpanded}>
          <Box px="md" py="sm">
            <RichTextDiagnosis
              value={frozenInterpretation}
              onChange={onFrozenInterpretationChange}
              placeholder="Enter case-level frozen interpretation..."
              minHeight={80}
              disabled={isReadOnly}
            />
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
}
